import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { timekeepingApi } from "../../services/timekeepingApi";
import { usePermissions } from "../../hooks/usePermissions";
import { StatusBadge } from "../../components/StatusBadge";
import { RefreshCw, GitMerge } from "lucide-react";
import { toast } from "sonner";
import { PageHelpBanner } from "../../components/PageHelpBanner";
import { RejectPairDialog } from "../../components/timekeeping/RejectPairDialog";
import { statusLabel } from "../../lib/statusLabel";
import { useLocalization } from "../../hooks/useLocalization";

export default function TimekeepingMonthlyPage() {
  const navigate = useNavigate();
  const { t } = useLocalization();
  const { canManageTimekeeping } = usePermissions();
  const [month, setMonth] = useState("2026-06");
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [linkingPairId, setLinkingPairId] = useState(null);
  const [employeeIdInput, setEmployeeIdInput] = useState("");
  const [rejectingPair, setRejectingPair] = useState(null);

  const load = () => {
    timekeepingApi.getMonthDashboard(month).then((r) => setDashboard(r.data)).catch(() => setDashboard(null));
  };

  useEffect(() => {
    if (!canManageTimekeeping) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canManageTimekeeping, month]);

  if (!canManageTimekeeping) return <p className="text-sm text-gray-500" data-testid="timekeeping-monthly-access-denied">{t("common.accessDenied")}</p>;

  const runPairing = async () => {
    setLoading(true);
    try {
      await timekeepingApi.runPairing(month);
      toast.success(t("mgmtTimekeeping.pairingComplete"));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("mgmtTimekeeping.pairingFailed"));
    } finally {
      setLoading(false);
    }
  };

  const confirmPair = async (pairId) => {
    try {
      await timekeepingApi.confirmPair(pairId, { employee_id: employeeIdInput || undefined });
      toast.success(t("mgmtTimekeeping.pairConfirmed"));
      setLinkingPairId(null);
      setEmployeeIdInput("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("mgmtTimekeeping.pairConfirmFailed"));
    }
  };

  const rejectPair = async (reason) => {
    await timekeepingApi.rejectPair(rejectingPair.id, reason);
    toast.success(t("mgmtTimekeeping.pairRejected"));
    load();
  };

  return (
    <div className="space-y-5" data-testid="timekeeping-monthly-page">
      <PageHelpBanner pageKey="timekeeping-monthly" title={t("pageHelp.defaultTitle")}>
        <p dangerouslySetInnerHTML={{ __html: t("mgmtTimekeeping.monthlyHelp") }} />
      </PageHelpBanner>
      <div>
        <h2 className="text-2xl font-bold tracking-tight text-gray-900">{t("mgmtTimekeeping.monthlyTitle")}</h2>
        <p className="text-sm text-gray-500">{t("mgmtTimekeeping.monthlySubtitle")}</p>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <label className="text-sm font-medium text-gray-600">{t("mgmtTimekeeping.payrollMonthLabel")}</label>
        <input data-testid="monthly-month-input" value={month} onChange={(e) => setMonth(e.target.value)} className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm w-32" />
        <button data-testid="monthly-refresh-btn" onClick={load} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border border-gray-200 hover:bg-gray-50">
          <RefreshCw size={13} /> {t("mgmtTimekeeping.refresh")}
        </button>
        <button data-testid="run-pairing-btn" disabled={loading} onClick={runPairing} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">
          <GitMerge size={13} /> {t("mgmtTimekeeping.runPairing")}
        </button>
      </div>

      {dashboard && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="monthly-dashboard-stats">
          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <p className="text-xs text-gray-500">{t("mgmtTimekeeping.filesRegistered")}</p>
            <p className="text-2xl font-bold text-gray-900">{dashboard.files_total}</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <p className="text-xs text-gray-500">{t("mgmtTimekeeping.pairs")}</p>
            <p className="text-2xl font-bold text-gray-900">{dashboard.pairs_total}</p>
          </div>
          {Object.entries(dashboard.files_by_status || {}).map(([status, count]) => (
            <div key={status} className="bg-white border border-gray-200 rounded-xl p-4">
              <p className="text-xs text-gray-500">{statusLabel(t, status)}</p>
              <p className="text-2xl font-bold text-gray-900">{count}</p>
            </div>
          ))}
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-gray-50 text-gray-500 text-left">
            <tr>
              <th className="px-4 py-3 font-medium">{t("mgmtTimekeeping.colEmployeeProposed")}</th>
              <th className="px-4 py-3 font-medium">{t("common.status")}</th>
              <th className="px-4 py-3 font-medium">{t("mgmtTimekeeping.colConfidence")}</th>
              <th className="px-4 py-3 font-medium">{t("mgmtTimekeeping.colSides")}</th>
              <th className="px-4 py-3 font-medium">{t("mgmtScheduling.colNotes")}</th>
              <th className="px-4 py-3 font-medium">{t("common.actions")}</th>
            </tr>
          </thead>
          <tbody>
            {(dashboard?.pairs || []).map((p) => (
              <tr key={p.id} className="border-t border-gray-100" data-testid={`pair-row-${p.id}`}>
                <td className="px-4 py-2 font-semibold text-gray-800">{p.employee_name || t("mgmtTimekeeping.unknown")}</td>
                <td className="px-4 py-2"><StatusBadge status={p.status} /></td>
                <td className="px-4 py-2 text-xs text-gray-500">{statusLabel(t, p.confidence)}</td>
                <td className="px-4 py-2 text-xs text-gray-500">
                  {p.blue_file_id ? "BLUE " : ""}{p.red_file_id ? "RED" : ""}{!p.blue_file_id && !p.red_file_id ? "-" : ""}
                </td>
                <td className="px-4 py-2 text-xs text-gray-500 max-w-xs truncate" title={p.notes}>{p.notes}</td>
                <td className="px-4 py-2 space-x-1">
                  {p.status !== "PAIRED" && p.status !== "PAIR_REJECTED" && (
                    linkingPairId === p.id ? (
                      <span className="inline-flex items-center gap-1">
                        <input
                          data-testid={`link-employee-input-${p.id}`}
                          placeholder={p.employee_id || t("mgmtTimekeeping.employeeIdPlaceholder")}
                          value={employeeIdInput}
                          onChange={(e) => setEmployeeIdInput(e.target.value)}
                          className="border border-gray-200 rounded px-2 py-1 text-xs w-28"
                        />
                        <button data-testid={`confirm-pair-btn-${p.id}`} onClick={() => confirmPair(p.id)} className="px-2 py-1 rounded text-xs font-semibold bg-green-600 text-white hover:bg-green-700">{t("mgmtTimekeeping.confirm")}</button>
                      </span>
                    ) : (
                      <button data-testid={`start-link-pair-btn-${p.id}`} onClick={() => { setLinkingPairId(p.id); setEmployeeIdInput(p.employee_id || ""); }} className="px-2 py-1 rounded text-xs font-semibold border border-gray-200 hover:bg-gray-50">
                        {p.employee_id ? t("mgmtTimekeeping.confirm") : t("mgmtTimekeeping.linkAndConfirm")}
                      </button>
                    )
                  )}
                  {p.employee_id && (
                    <button data-testid={`review-pair-btn-${p.id}`} onClick={() => navigate(`/management/timekeeping/review/${p.employee_id}?payroll_month=${month}`)} className="px-2 py-1 rounded text-xs font-semibold bg-gray-900 text-white hover:bg-gray-700">
                      {t("mgmtTimekeeping.review")}
                    </button>
                  )}
                  {p.status !== "PAIR_REJECTED" && (
                    <button data-testid={`reject-pair-btn-${p.id}`} onClick={() => setRejectingPair(p)} className="px-2 py-1 rounded text-xs font-semibold border border-red-200 text-red-600 hover:bg-red-50">
                      {t("common.reject")}
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {(!dashboard || (dashboard.pairs || []).length === 0) && (
              <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-500" data-testid="no-pairs-row">{t("mgmtTimekeeping.noPairs")}</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <RejectPairDialog open={!!rejectingPair} onOpenChange={(next) => !next && setRejectingPair(null)} pair={rejectingPair} month={month} onSubmit={rejectPair} />
    </div>
  );
}
